import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsApi, appointmentApi, patientApi, aiApi, messageApi, cdsApi, physicianApi, aiRecommendationsApi, taskApi, formAssignmentApi } from '../../lib/api';
import api from '../../lib/api';
import { updateTaskResilient } from '../../lib/offlineQueue';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Calendar, FileText, Activity, AlertTriangle, Users, Clock,
  ArrowRight, Send, Brain, Sparkles, MessageSquare, ChevronRight,
  Shield, Pill, Heart, Zap, CheckCircle, XCircle, Info, ClipboardList
} from 'lucide-react';
import WorkedCasesChart from './WorkedCasesChart';
import FormsInProgressWidget from '../../components/dashboard/FormsInProgressWidget';
import { toast } from 'sonner';

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

export default function PhysicianDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [highRiskPatients, setHighRiskPatients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // AI Chat state
  const [aiMessage, setAiMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [cdsData, setCdsData] = useState(null);
  const [cdsLoading, setCdsLoading] = useState(false);
  const [selectedCdsPatient, setSelectedCdsPatient] = useState(null);
  const [vitalsSummary, setVitalsSummary] = useState([]);
  const [vitalsAlerts, setVitalsAlerts] = useState([]);
  const [clinicalRecs, setClinicalRecs] = useState([]);
  // Workload state — tasks, upcoming form assignments, and user activity timeline
  const [tasks, setTasks] = useState([]);
  const [upcomingForms, setUpcomingForms] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, appointmentsRes, messagesRes] = await Promise.all([
        analyticsApi.provider(user.id),
        appointmentApi.list({ provider_id: user.id, date: new Date().toISOString().split('T')[0] }),
        messageApi.inbox()
      ]);
      
      setAnalytics(analyticsRes.data);
      setAppointments(appointmentsRes.data.appointments || []);
      setHighRiskPatients(analyticsRes.data.high_risk_patients || []);
      setMessages(messagesRes.data.messages?.slice(0, 5) || []);
      
      // Load vitals + recommendations in background
      try {
        const [vitalsRes, recsRes] = await Promise.all([
          physicianApi.getVitalsSummary(),
          aiRecommendationsApi.get('physician')
        ]);
        setVitalsSummary(vitalsRes.data.patients || []);
        setVitalsAlerts(vitalsRes.data.alerts || []);
        setClinicalRecs(recsRes.data.recommendations || []);
      } catch (e) { /* non-critical */ }

      // Load workload (tasks + upcoming forms + activity timeline) in parallel
      try {
        const [tasksRes, formsRes, activityRes] = await Promise.all([
          taskApi.list().catch(() => ({ data: { tasks: [] } })),
          formAssignmentApi.list({ status: 'pending', due_within_days: 7 }).catch(() => ({ data: { assignments: [] } })),
          api.get('/auth/activity?limit=100').catch(() => ({ data: { logs: [] } })),
        ]);
        setTasks(tasksRes.data.tasks || []);
        setUpcomingForms(formsRes.data.assignments || []);
        setActivity(activityRes.data.logs || []);
      } catch (e) { /* non-critical */ }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAiChat = async (e) => {
    e.preventDefault();
    if (!aiMessage.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await aiApi.chat(aiMessage);
      setAiResponse(response.data.response);
      setAiMessage('');
    } catch (error) {
      toast.error('AI assistant temporarily unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  const completeTask = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await updateTaskResilient(api, taskId, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch { /* ignore */ }
  };

  const pendingTasks = useMemo(() =>
    tasks.filter(t => t.status !== 'completed').sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3)),
    [tasks]
  );
  const completedToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return tasks.filter(t => t.status === 'completed' && (t.updated_at || t.created_at || '').slice(0, 10) === today);
  }, [tasks]);

  // Activity logs drive the WorkedCasesChart drill-down
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#64748B]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="physician-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, Dr. {user?.name?.split(' ').pop()}
        </h1>
        <p className="text-[#64748B] mt-1">
          You have <strong>{analytics?.appointments_today || 0}</strong> appointments today and{' '}
          <strong className={analytics?.unsigned_notes > 0 ? 'text-[#991B1B]' : ''}>{analytics?.unsigned_notes || 0}</strong> unsigned notes.
        </p>
      </div>

      {/* Quick Stats - Clickable Drill-downs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/appointments')} data-testid="drilldown-todays-patients">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#0E7490]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{analytics?.appointments_today || 0}</div>
                <div className="text-xs text-[#64748B]">Today's Patients</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#0E7490] flex items-center gap-1">View schedule <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
        
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/patients')} data-testid="drilldown-patient-panel">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1A56DB]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#1A56DB]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{analytics?.patient_panel_size || 0}</div>
                <div className="text-xs text-[#64748B]">Patient Panel</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#1A56DB] flex items-center gap-1">View patients <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
        
        <Card className={`careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow ${analytics?.unsigned_notes > 0 ? 'border-[#991B1B] border-2' : ''}`} onClick={() => navigate('/notes')} data-testid="drilldown-unsigned-notes">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                analytics?.unsigned_notes > 0 ? 'bg-[#991B1B]/10' : 'bg-[#166534]/10'
              }`}>
                <FileText className={`w-5 h-5 ${analytics?.unsigned_notes > 0 ? 'text-[#991B1B]' : 'text-[#166534]'}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${analytics?.unsigned_notes > 0 ? 'text-[#991B1B]' : 'text-[#0A2342]'}`}>
                  {analytics?.unsigned_notes || 0}
                </div>
                <div className="text-xs text-[#64748B]">Unsigned Notes</div>
              </div>
            </div>
            <div className={`mt-2 text-xs flex items-center gap-1 ${analytics?.unsigned_notes > 0 ? 'text-[#991B1B]' : 'text-[#166534]'}`}>Review notes <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
        
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/patients?risk=high')} data-testid="drilldown-high-risk">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#991B1B]/10 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[#991B1B]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{highRiskPatients.length}</div>
                <div className="text-xs text-[#64748B]">High Risk</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#991B1B] flex items-center gap-1">View at-risk <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
      </div>

      {/* Forms In Progress */}
      <FormsInProgressWidget />

      {/* Clinical Alerts */}
      {vitalsAlerts.length > 0 && (
        <Card className="careiq-card border-l-4 border-l-[#991B1B]" data-testid="clinical-alerts">          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#991B1B]" />
              <span className="font-semibold text-sm text-[#991B1B]">Clinical Alerts ({vitalsAlerts.length})</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {vitalsAlerts.map((a, i) => (
                <div key={i} className={`flex items-center gap-2 p-2 rounded-lg ${a.severity === 'critical' ? 'bg-[#991B1B]/10' : 'bg-[#92400E]/10'}`} onClick={() => navigate(`/patients/${a.patient_id}`)} style={{ cursor: 'pointer' }}>
                  <div className={`w-2 h-2 rounded-full ${a.severity === 'critical' ? 'bg-[#991B1B] animate-pulse' : 'bg-[#92400E]'}`} />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-[#0A2342] truncate">{a.patient}</div>
                    <div className="text-[10px] text-[#64748B]">{a.type === 'high_bp' ? 'High BP' : a.type === 'low_o2' ? 'Low O2' : 'Abnormal HR'}: <span className="font-mono">{a.value}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Workload — tasks, upcoming forms, and activity timeline */}
      <div className="grid lg:grid-cols-3 gap-6" data-testid="physician-workload-section">
        {/* Pending Tasks */}
        <Card className="careiq-card" data-testid="physician-pending-tasks-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-[#0E7490]" />
              My Tasks
              {pendingTasks.length > 0 && <span className="text-xs font-normal text-[#64748B]">({pendingTasks.length} pending · {completedToday.length} done today)</span>}
            </CardTitle>
            <Link to="/tasks" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {pendingTasks.length === 0 ? (
              <div className="text-center py-6 text-sm text-[#94A3B8]">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                All caught up — no pending tasks.
              </div>
            ) : (
              <div className="space-y-2">
                {pendingTasks.slice(0, 6).map((task) => (
                  <div
                    key={task.id}
                    className={`flex items-start gap-3 p-2 rounded-lg border transition-all ${
                      task.priority === 'high'
                        ? 'border-[#991B1B]/40 bg-[#991B1B]/5'
                        : 'border-[#E2E8F0] hover:bg-[#F8FAFC]'
                    }`}
                    data-testid={`physician-task-row-${task.id}`}
                  >
                    <Checkbox
                      checked={task.status === 'completed'}
                      onCheckedChange={() => completeTask(task.id, task.status)}
                      className="mt-0.5"
                      data-testid={`physician-task-checkbox-${task.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#0A2342] text-sm truncate">{task.title}</div>
                      <div className="text-[10px] text-[#64748B] truncate">
                        {task.patient_name ? `Patient: ${task.patient_name}` : (task.form_name || 'No patient linked')}
                        {task.due_date && <span> · Due {new Date(task.due_date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium shrink-0 ${
                      task.priority === 'high' ? 'bg-[#991B1B] text-white' :
                      task.priority === 'medium' ? 'bg-[#92400E] text-white' :
                      'bg-[#64748B] text-white'
                    }`}>
                      {task.priority || 'medium'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Forms */}
        <Card className="careiq-card" data-testid="physician-upcoming-forms-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0E7490]" />
              Upcoming Forms
            </CardTitle>
            <Link to="/forms" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {upcomingForms.length === 0 ? (
              <div className="text-center py-6 text-sm text-[#94A3B8]">
                <FileText className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                No forms due in the next 7 days.
              </div>
            ) : (
              <div className="space-y-1.5">
                {upcomingForms.slice(0, 6).map((a) => {
                  const due = a.due_date ? new Date(a.due_date) : null;
                  const isOverdue = due && due < new Date();
                  return (
                    <Link
                      key={a.id}
                      to={a.form_id ? `/forms/${a.form_id}/fill?patientId=${a.patient_id || ''}` : '/forms'}
                      className="flex items-center justify-between p-2 rounded border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                      data-testid={`physician-upcoming-form-${a.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-[#0A2342] truncate">{a.form_name || 'Form'}</div>
                        <div className="text-[10px] text-[#64748B] truncate">{a.patient_name || 'Unassigned'}</div>
                      </div>
                      <div className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${
                        isOverdue ? 'bg-[#991B1B]/10 text-[#991B1B]' : 'bg-[#0E7490]/10 text-[#0E7490]'
                      }`}>
                        {due ? (isOverdue ? 'Overdue' : due.toLocaleDateString()) : '—'}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline Chart — interactive drill-down */}
        <div data-testid="physician-timeline-chart-card">
          <WorkedCasesChart activity={activity} title="Worked Cases — 7 Days" height={200} testId="physician-worked-cases-chart" />
        </div>
      </div>

      {/* Patient Vitals Summary */}
      {vitalsSummary.length > 0 && (
        <Card className="careiq-card" data-testid="patient-vitals-panel">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#991B1B]" /> Patient Vitals Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[#F8FAFC]">
                    <th className="p-2 text-left text-[#64748B] text-xs font-medium">Patient</th>
                    <th className="p-2 text-center text-[#64748B] text-xs font-medium">BP</th>
                    <th className="p-2 text-center text-[#64748B] text-xs font-medium">HR</th>
                    <th className="p-2 text-center text-[#64748B] text-xs font-medium">SpO2</th>
                    <th className="p-2 text-center text-[#64748B] text-xs font-medium">Temp</th>
                    <th className="p-2 text-center text-[#64748B] text-xs font-medium">Weight</th>
                    <th className="p-2 text-center text-[#64748B] text-xs font-medium">Meds</th>
                    <th className="p-2 text-center text-[#64748B] text-xs font-medium">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {vitalsSummary.map((p) => {
                    const v = p.latest_vitals;
                    const bpHigh = v?.bp_systolic >= 140;
                    const o2Low = v?.oxygen_saturation && v.oxygen_saturation <= 94;
                    const hrAbn = v?.heart_rate && (v.heart_rate > 100 || v.heart_rate < 50);
                    return (
                      <tr key={p.patient_id} className="border-b hover:bg-[#F8FAFC] cursor-pointer" onClick={() => navigate(`/patients/${p.patient_id}`)} data-testid={`vitals-row-${p.patient_id}`}>
                        <td className="p-2">
                          <div className="font-medium text-[#0A2342] text-xs">{p.name}</div>
                        </td>
                        <td className={`p-2 text-center font-mono text-xs ${bpHigh ? 'text-[#991B1B] font-bold' : 'text-[#0A2342]'}`}>
                          {v ? `${v.bp_systolic}/${v.bp_diastolic}` : '-'}
                        </td>
                        <td className={`p-2 text-center font-mono text-xs ${hrAbn ? 'text-[#92400E] font-bold' : 'text-[#0A2342]'}`}>
                          {v?.heart_rate || '-'}
                        </td>
                        <td className={`p-2 text-center font-mono text-xs ${o2Low ? 'text-[#991B1B] font-bold' : 'text-[#0A2342]'}`}>
                          {v?.oxygen_saturation ? `${v.oxygen_saturation}%` : '-'}
                        </td>
                        <td className="p-2 text-center font-mono text-xs text-[#0A2342]">{v?.temperature ? `${v.temperature}F` : '-'}</td>
                        <td className="p-2 text-center font-mono text-xs text-[#0A2342]">{v?.weight ? `${v.weight}lb` : '-'}</td>
                        <td className="p-2 text-center text-xs text-[#0A2342]">{p.active_meds}</td>
                        <td className="p-2 text-center">
                          <span className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                            p.risk_score >= 70 ? 'bg-[#991B1B]/10 text-[#991B1B]' :
                            p.risk_score >= 40 ? 'bg-[#92400E]/10 text-[#92400E]' :
                            'bg-[#166534]/10 text-[#166534]'
                          }`}>{p.risk_score}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Recommendations for Physician */}
      {clinicalRecs.length > 0 && (
        <Card className="careiq-card" data-testid="physician-ai-recs">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#7C3AED]" /> AI Clinical Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              {clinicalRecs.map((rec, i) => (
                <div key={i} className={`p-3 rounded-xl border-l-4 ${
                  rec.priority === 'critical' ? 'border-l-[#991B1B] bg-[#991B1B]/5' :
                  rec.priority === 'high' ? 'border-l-[#92400E] bg-[#92400E]/5' :
                  'border-l-[#0E7490] bg-[#0E7490]/5'
                }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 text-[9px] rounded-full font-medium ${
                      rec.priority === 'critical' || rec.priority === 'high' ? 'bg-[#991B1B]/10 text-[#991B1B]' : 'bg-[#0E7490]/10 text-[#0E7490]'
                    }`}>{rec.priority}</span>
                    <span className="text-[10px] text-[#64748B]">{rec.category}</span>
                  </div>
                  <div className="font-medium text-xs text-[#0A2342]">{rec.title}</div>
                  <p className="text-[11px] text-[#64748B] mt-0.5">{rec.description}</p>
                  {rec.actions && rec.actions.slice(0, 2).map((a, j) => (
                    <div key={j} className="flex items-center gap-1 text-[11px] text-[#0E7490] mt-1"><ArrowRight className="w-3 h-3" />{a}</div>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Today's Schedule */}
        <Card className="lg:col-span-2 careiq-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Today's Schedule</CardTitle>
            <Link to="/appointments" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              Full schedule <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {appointments.length > 0 ? (
              <div className="space-y-3">
                {appointments.map((appt, index) => (
                  <div 
                    key={appt.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md ${
                      index === 0 ? 'bg-[#0E7490]/5 border-[#0E7490]' : 'border-[#E2E8F0]'
                    }`}
                  >
                    <div className="text-center min-w-[60px]">
                      <div className="text-lg font-semibold text-[#0A2342]">{appt.scheduled_time}</div>
                      <div className="text-xs text-[#64748B]">{appt.duration_minutes} min</div>
                    </div>
                    <div className="flex-1">
                      <Link 
                        to={`/patients/${appt.patient_id}`}
                        className="font-medium text-[#0A2342] hover:text-[#0E7490]"
                      >
                        {appt.patient_name}
                      </Link>
                      <div className="text-sm text-[#64748B]">
                        {appt.reason || appt.appointment_type.replace('_', ' ')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        appt.appointment_type === 'telehealth' ? 'bg-[#1A56DB]/10 text-[#1A56DB]' :
                        'bg-[#0E7490]/10 text-[#0E7490]'
                      }`}>
                        {appt.appointment_type.replace('_', ' ')}
                      </span>
                      {index === 0 && (
                        <Button size="sm" className="careiq-btn-secondary" data-testid="start-encounter-btn">
                          Start
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#64748B]">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
                <p>No appointments scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* High Risk Patients */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#991B1B]" />
              At-Risk Patients
            </CardTitle>
          </CardHeader>
          <CardContent>
            {highRiskPatients.length > 0 ? (
              <div className="space-y-3">
                {highRiskPatients.map((patient) => (
                  <Link
                    key={patient.id}
                    to={`/patients/${patient.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-[#F8FAFC] transition-colors group"
                  >
                    <div>
                      <div className="font-medium text-[#0A2342] group-hover:text-[#0E7490]">
                        {patient.name}
                      </div>
                      <div className="text-xs text-[#64748B]">Risk Score: {patient.risk_score}</div>
                    </div>
                    <span className="risk-badge-high">{patient.risk_score}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-[#64748B]">
                <Activity className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                <p className="text-sm">No high-risk patients</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Clinical Decision Support */}
      <Card className="careiq-card border-l-4 border-l-[#7C3AED]" data-testid="cds-widget">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Brain className="w-5 h-5 text-[#7C3AED]" />
            AI Clinical Decision Support
            <span className="text-xs bg-[#7C3AED]/10 text-[#7C3AED] px-2 py-0.5 rounded-full">AI-Powered</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <select
              value={selectedCdsPatient || ''}
              onChange={(e) => setSelectedCdsPatient(e.target.value)}
              className="flex-1 rounded-md border border-[#E2E8F0] p-2 text-sm"
              data-testid="cds-patient-select"
            >
              <option value="">Select a patient for CDS analysis...</option>
              {highRiskPatients.map(p => (
                <option key={p.id} value={p.id}>{p.name} (Risk: {p.risk_score})</option>
              ))}
            </select>
            <Button
              className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white"
              disabled={!selectedCdsPatient || cdsLoading}
              onClick={async () => {
                setCdsLoading(true);
                try {
                  const res = await cdsApi.analyze(selectedCdsPatient);
                  setCdsData(res.data);
                } catch (err) {
                  toast.error('CDS analysis failed');
                } finally {
                  setCdsLoading(false);
                }
              }}
              data-testid="cds-analyze-btn"
            >
              {cdsLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <><Zap className="w-4 h-4 mr-1" /> Analyze</>
              )}
            </Button>
          </div>

          {cdsData && (
            <div className="space-y-4" data-testid="cds-results">
              <div className="p-3 bg-[#F8FAFC] rounded-lg">
                <div className="text-xs text-[#64748B] mb-1">Overall Assessment</div>
                <div className="text-sm font-medium text-[#0A2342]">{cdsData.cds?.overall_risk_assessment || 'Analysis complete'}</div>
              </div>

              {cdsData.cds?.drug_interactions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#0A2342] mb-2 flex items-center gap-1">
                    <Pill className="w-4 h-4 text-[#991B1B]" /> Drug Interactions
                  </h4>
                  <div className="space-y-2">
                    {cdsData.cds.drug_interactions.map((di, i) => (
                      <div key={i} className={`p-3 rounded-lg ${di.severity === 'high' ? 'bg-[#991B1B]/5' : di.severity === 'medium' ? 'bg-[#92400E]/5' : 'bg-[#F8FAFC]'}`} style={{ borderLeft: `3px solid ${di.severity === 'high' ? '#991B1B' : di.severity === 'medium' ? '#92400E' : '#64748B'}` }}>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold uppercase ${di.severity === 'high' ? 'text-[#991B1B]' : di.severity === 'medium' ? 'text-[#92400E]' : 'text-[#64748B]'}`}>{di.severity}</span>
                          <span className="text-xs text-[#64748B]">{di.drugs?.join(' + ')}</span>
                        </div>
                        <p className="text-xs text-[#334155] mt-1">{di.description}</p>
                        <p className="text-xs text-[#0E7490] mt-1">{di.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cdsData.cds?.risk_alerts?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#0A2342] mb-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-[#92400E]" /> Risk Alerts
                  </h4>
                  <div className="space-y-2">
                    {cdsData.cds.risk_alerts.map((alert, i) => (
                      <div key={i} className={`p-3 rounded-lg flex items-start gap-2 ${alert.level === 'critical' ? 'bg-[#991B1B]/5' : alert.level === 'warning' ? 'bg-[#92400E]/5' : 'bg-[#F8FAFC]'}`}>
                        {alert.level === 'critical' ? <XCircle className="w-4 h-4 text-[#991B1B] mt-0.5 flex-shrink-0" /> : alert.level === 'warning' ? <AlertTriangle className="w-4 h-4 text-[#92400E] mt-0.5 flex-shrink-0" /> : <Info className="w-4 h-4 text-[#0E7490] mt-0.5 flex-shrink-0" />}
                        <div>
                          <div className="text-xs font-medium text-[#0A2342]">{alert.title}</div>
                          <div className="text-xs text-[#64748B]">{alert.description}</div>
                          <div className="text-xs text-[#0E7490] mt-1">{alert.action}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cdsData.cds?.care_suggestions?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#0A2342] mb-2 flex items-center gap-1">
                    <Heart className="w-4 h-4 text-[#166534]" /> Care Suggestions
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {cdsData.cds.care_suggestions.map((sug, i) => (
                      <div key={i} className="p-3 rounded-lg bg-[#166534]/5 border border-[#166534]/10">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sug.priority === 'high' ? 'bg-[#991B1B]/10 text-[#991B1B]' : sug.priority === 'medium' ? 'bg-[#92400E]/10 text-[#92400E]' : 'bg-[#166534]/10 text-[#166534]'}`}>{sug.priority}</span>
                          <span className="text-[10px] text-[#64748B]">{sug.category}</span>
                        </div>
                        <div className="text-xs font-medium text-[#0A2342] mt-1">{sug.title}</div>
                        <div className="text-[10px] text-[#64748B] mt-1">{sug.evidence_basis}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {cdsData.cds?.medication_review && (
                <div className="p-3 rounded-lg bg-[#F8FAFC] flex items-center gap-3">
                  <Shield className={`w-5 h-5 ${cdsData.cds.medication_review.status === 'optimal' ? 'text-[#166534]' : cdsData.cds.medication_review.status === 'concerns' ? 'text-[#991B1B]' : 'text-[#92400E]'}`} />
                  <div>
                    <div className="text-xs font-medium text-[#0A2342]">Medication Review: <span className="capitalize">{cdsData.cds.medication_review.status}</span></div>
                    <div className="text-[10px] text-[#64748B]">{cdsData.cds.medication_review.notes}</div>
                  </div>
                </div>
              )}

              <div className="text-[10px] text-[#94A3B8] italic">{cdsData.disclaimer}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Messages */}
        <Card className="careiq-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Recent Messages</CardTitle>
            <Link to="/messages" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent>
            {messages.length > 0 ? (
              <div className="space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors cursor-pointer ${
                      !msg.read ? 'bg-[#0E7490]/5' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-[#0A2342]">{msg.sender_name}</div>
                        <div className="text-sm text-[#334155]">{msg.subject}</div>
                        <div className="text-xs text-[#64748B] mt-1 line-clamp-1">{msg.preview}</div>
                      </div>
                      {!msg.read && <div className="w-2 h-2 bg-[#0E7490] rounded-full" />}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-[#64748B]">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
                <p className="text-sm">No messages</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Clinical Assistant */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#0E7490]" />
              AI Clinical Assistant
              <span className="text-xs bg-[#0E7490]/10 text-[#0E7490] px-2 py-0.5 rounded-full">Beta</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48 mb-4 p-3 bg-[#F8FAFC] rounded-lg">
              {aiResponse ? (
                <div className="text-sm text-[#334155] whitespace-pre-wrap">{aiResponse}</div>
              ) : (
                <div className="text-center text-[#64748B] py-8">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 text-[#0E7490]" />
                  <p className="text-sm">Ask me about patient cases, drug interactions, or clinical guidelines.</p>
                </div>
              )}
            </ScrollArea>
            <form onSubmit={handleAiChat} className="flex gap-2">
              <Input
                placeholder="Ask about a patient case..."
                value={aiMessage}
                onChange={(e) => setAiMessage(e.target.value)}
                className="careiq-input"
                disabled={aiLoading}
                data-testid="ai-chat-input"
              />
              <Button 
                type="submit" 
                className="careiq-btn-secondary"
                disabled={aiLoading || !aiMessage.trim()}
                data-testid="ai-chat-submit"
              >
                {aiLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
